import React, { useContext, useReducer } from "react";
import { useExternalEntities } from "../ExternalEntitiesProvider/ExternalEntitiesProvider";

/**
 * Interface for operation state data.
 *
 * ============= NOTICE =============
 * | This is a snapshot of interface OperationStateData from
 * | core-client-telemetry and mean to expose to other partner of TwoUI. This is
 * | NOT meant to be in sync with core-client-telemetry interfaces.
 * ==========================================
 */
interface TwoUIOperationStateData {
    /**
     * Status of operation
     */
    readonly status: TwoUIOperationStatus;
    /**
     * Optional error information for a failed operation
     */
    readonly error?: Error;
    /**
     * Custom data for a finished operation
     */
    readonly customData?: any;
}

/**
 * Interface for operation state object.
 * A dictionary with key: name, val: status
 *
 * ============= NOTICE =============
 * | This is a snapshot of interface OperationState from
 * | core-client-telemetry and mean to expose to other partner of TwoUI. This is
 * | NOT meant to be in sync with core-client-telemetry interfaces.
 * ==========================================
 */
export interface TwoUIOperationState {
    readonly [operationName: string]: TwoUIOperationStateData | undefined;
}

/**
 * Different operation state
 *
 * ============= NOTICE =============
 * | This is a snapshot of interface OperationStatus from
 * | core-client-telemetry and mean to expose to other partner of TwoUI. This is
 * | NOT meant to be in sync with core-client-telemetry interfaces.
 * ==========================================
 */
export type TwoUIOperationStatus = "inert" | "first-load" | "updating" | "finished" | "failed" | "update-failed";

/**
 * Object to be passed when calling dispatch
 *
 * ============= NOTICE =============
 * | This is a snapshot of interface OperationAction from
 * | core-client-telemetry and mean to expose to other partner of TwoUI. This is
 * | NOT meant to be in sync with core-client-telemetry interfaces.
 * ==========================================
 */
export type TwoUIOperationAction = {
    /**
     * Name of tracked operation
     */
    readonly name: string,
    /**
     * Status of operation
     */
    readonly status: TwoUIOperationStatus
    /**
     * Optional error information for a failed operation
     */
    readonly error?: Error
    /**
     * Custom data for a finished operation
     */
    readonly customData?: any;
};

export type TwoUIOperationDispatcher = React.Dispatch<TwoUIOperationAction>;
export type TwoUIOperations = readonly [TwoUIOperationState, TwoUIOperationDispatcher];

export const twoUIOperationStateContext = React.createContext<TwoUIOperationState | null>(null);
export const twoUIOperationDispatchContext = React.createContext<TwoUIOperationDispatcher | null>(null);

// when dispatch is called, copy the current state into a new obj
// then update status of [action.name] to action.status in the new state.
// this will not create duplicate entries for the same operation names,
// but instead update it in place.
const twoUIOperationsReducer = (curr_state: TwoUIOperationState, action: TwoUIOperationAction): TwoUIOperationState => {
    return {
        ...curr_state,
        [action.name]: {
            status: action.status,
            error: action.error,
            customData: action.customData
        }
    };
};

/**
 * Context provider for telemetry in TwoUI
 */
export const TwoUIOperationsProvider: React.FC = ({
    children
}) => {

    const [state, dispatch] = useReducer(twoUIOperationsReducer, {});

    return (
        <twoUIOperationDispatchContext.Provider value={dispatch}>
            <twoUIOperationStateContext.Provider value={state}>
                {children}
            </twoUIOperationStateContext.Provider>
        </twoUIOperationDispatchContext.Provider>
    );
};

export const useTwoUIOperationStates: () => TwoUIOperationState = () => {
    const operationStateContext = twoUIOperationStateContext;

    const operationStates = useContext(operationStateContext);

    if (!operationStates) {
        throw new Error("TwoUIOperation: Context is uninitialized. Confirm that your component is contained within an TwoUIOperationsProvider component.");
    }

    return operationStates;
};

export const useTwoUIOperationDispatcher: () => TwoUIOperationDispatcher = () => {
    const { operationEntities } = useExternalEntities();

    let operationDispatchContext = twoUIOperationDispatchContext;

    if (operationEntities) {
        operationDispatchContext = operationEntities.operationDispatchContext;
    }

    const dispatcher = useContext(operationDispatchContext);

    if (!dispatcher) {
        throw new Error("TwoUIOperation: Context is uninitialized. Confirm that your component is contained within an TwoUIOperationsProvider component.");
    }

    return dispatcher;
};
